import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { auth, googleProvider, signInWithPopup } from '../firebase';
import { BLOG_DATA } from '../lib/blogData';
import { YachtLogo } from '../components/YachtLogo';

import Navbar from '../components/Navbar';

export default function Blog() {
  const navigate = useNavigate();

  const handleGetStarted = async () => {
    if (!auth.currentUser) {
      try {
        await signInWithPopup(auth, googleProvider);
        navigate('/studio');
      } catch (error) {
        console.error("Error signing in:", error);
      }
    } else {
      navigate('/studio');
    }
  };

  const posts = Object.entries(BLOG_DATA).map(([slug, data]) => ({
    slug,
    ...data
  }));

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-zinc-100 selection:text-black flex flex-col">
      <Navbar />

      {/* Main Content */}
      <main className="flex-1 pt-32 pb-24 px-8 max-w-5xl mx-auto w-full">
        <header className="mb-16">
          <h1 className="text-5xl md:text-6xl font-mono uppercase tracking-tighter mb-6">The Ship Log</h1>
          <p className="text-xl text-zinc-400 font-light max-w-2xl">
            Thoughts, playbooks, and contrarian takes on building, shipping, and growing software products at lightspeed.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.map((post) => (
            <Link 
              key={post.slug} 
              to={`/blog/${post.slug}`}
              className="group flex flex-col bg-[#111111] border border-[#222222] rounded-2xl overflow-hidden hover:border-zinc-700 transition-all"
            >
              <div className="h-48 overflow-hidden relative">
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10" />
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2 py-1 bg-zinc-900 border border-zinc-800 rounded text-[10px] font-mono uppercase tracking-widest text-zinc-300">
                    {post.tag}
                  </span>
                  <span className="text-xs font-mono text-zinc-500">{post.date}</span>
                </div>
                <h2 className="text-2xl font-mono uppercase tracking-tighter mb-4 group-hover:text-[#0074D9] transition-colors">
                  {post.title}
                </h2>
                <div className="mt-auto flex items-center justify-between text-xs font-mono uppercase tracking-widest text-zinc-500">
                  <span>{post.readTime}</span>
                  <span className="flex items-center gap-1 group-hover:text-white transition-colors">
                    Read Post <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#222222] bg-[#0A0A0A] py-12 px-8">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <YachtLogo className="w-4 h-4 opacity-50" />
            <span className="font-mono text-sm tracking-tighter uppercase text-zinc-500">Yacht Labs © 2026</span>
          </div>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link to="/product" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">Product</Link>
            <Link to="/use-cases" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">Use Cases</Link>
            <Link to="/solutions" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">Solutions</Link>
            <Link to="/resources" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">Resources</Link>
            <Link to="/about" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">Company</Link>
            <Link to="/pricing" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
